import React, { useEffect, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router";
import { bindActionCreators, Dispatch } from "redux";
import styled from "styled-components";
import { generateDurationOptions, generateTimeOptions } from "sweet-global";
import {
  displayPrice,
  estimatePrice,
  getOptionTitle,
  NewInquiry
} from "sweet-global";
import GuestsIcon from "sweet-web/components/icons/GuestsIcon";
import LightningIcon from "sweet-web/components/icons/LightningIcon";
import Button from "sweet-web/components/universal/Button";
import Input from "sweet-web/components/universal/Input";
import DateInput from "sweet-web/components/universal/Input/DateInput";
import OptionInput from "sweet-web/components/universal/Input/OptionInput";
import SelectInput from "sweet-web/components/universal/Input/SelectInput";
import {
  Label,
  SectionTitle,
  Title
} from "sweet-web/components/universal/Text";
import { colors } from "sweet-web/lib/styles";
import { RootState } from "sweet-web/redux";
import { CreateChat } from "sweet-web/redux/chat/actions";
import * as selectors from "sweet-web/redux/selectors";

const Wrapper = styled.div`
  width: 100%;
  @media screen and (max-width: 600px) {
    padding-bottom: 180px;
  }
`;

const PreviewTitleWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding-bottom: 15px;
`;

const Pricing = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 10px 0 5px 0;
`;

const PriceEstimate = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const PriceEstimateTitle = styled.h5<{ active: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${props => (props.active ? colors.nearBlack : colors.gray)};
  transition: all 150ms cubic-bezier(0.21, 0.94, 0.64, 0.99);
`;

const PriceEstimateAmount = styled.h5`
  font-size: 17px;
  font-weight: 700;
  color: ${colors.primary};
  margin: 5px 0;
`;

const InstantQuote = styled.span`
  padding: 5px 10px;
  background-color: ${colors.lighterGray};
  font-size: 14px;
  font-weight: 600;
  color: ${colors.gray};
  border-radius: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 150ms cubic-bezier(0.21, 0.94, 0.64, 0.99);
  &:hover {
    cursor: default;
    /* transform: scale(1.03); */
  }
`;

const PriceMinimum = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const PriceMinimumTitle = styled.h5`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.gray};
`;

const PriceMinimumAmount = styled.h5`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.gray};
`;

const InputRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const LowerWrapper = styled.div`
  width: 100%;
  @media screen and (max-width: 600px) {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 0px 20px 10px 20px;
    background: ${colors.white};
    border-top: 1px solid ${colors.lightGray};
  }
`;

const initialInquiry: NewInquiry = {
  date: "",
  startTime: "20:00",
  length: 3,
  guests: null,
  spaces: {},
  options: {},
  comments: ""
};

type OwnProps = {
  venueId: string;
  preview?: boolean;
};
type Props = OwnProps & RouteComponentProps & ConnectedProps<typeof connector>;

const BookPricing: React.FC<Props> = props => {
  const [inquiry, setInquiry] = useState(initialInquiry);
  const { venue, loggedIn, filters, loading } = props;
  useEffect(() => {
    const updateObj: any = {};
    if (filters.guests) updateObj.guests = filters.guests;
    if (filters.date) updateObj.date = filters.date;
    setInquiry(i => ({
      ...i,
      ...updateObj
    }));
  }, [filters]);

  const estimatedPrice = estimatePrice(venue, inquiry);

  if (!venue) {
    return null;
  }

  const timeOptions = generateTimeOptions();
  const durationOptions = generateDurationOptions();

  const submitInquiry = async e => {
    e.preventDefault();
    if (props.preview) return;

    try {
      if (!loggedIn) {
        alert("Create an account or login to request a booking");
        props.history.push("/join");

        return;
      }

      const chatId = await props.CreateChat(props.venueId, inquiry);
      if (chatId) {
        props.history.push(`/c/${chatId}`);
      }
    } catch (error) {
      alert(error);
    }
  };

  return (
    <Wrapper>
      {props.preview && (
        <PreviewTitleWrapper>
          <SectionTitle style={{ marginBottom: 0 }}>
            Preview Booking
          </SectionTitle>
          <Label>Your booking form as customers would see it</Label>
        </PreviewTitleWrapper>
      )}
      <InputRow>
        <DateInput
          title="Date"
          date={inquiry.date}
          onDateChange={newDate =>
            setInquiry({
              ...inquiry,
              date: newDate
            })
          }
          style={{ flex: 1, marginRight: 5 }}
        />
        <Input
          style={{ flex: 1, marginLeft: 5 }}
          icon={<GuestsIcon size={14} color={colors.gray} />}
          title={"Guests"}
          type="number"
          value={inquiry.guests ? String(inquiry.guests) : ""}
          onChange={value =>
            setInquiry({
              ...inquiry,
              guests: Number(value)
            })
          }
        />
      </InputRow>

      <InputRow>
        <SelectInput
          style={{ flex: 1, marginRight: 5 }}
          title="Start Time"
          value={inquiry.startTime}
          options={timeOptions}
          onChange={value => {
            setInquiry(i => ({
              ...i,
              startTime: value
            }));
          }}
        />
        <SelectInput
          style={{ flex: 1, marginLeft: 5 }}
          title="Duration"
          value={inquiry.length ? String(inquiry.length) : ""}
          options={durationOptions}
          onChange={value => {
            setInquiry(i => ({
              ...i,
              length: Number(value)
            }));
          }}
        />
      </InputRow>

      {Object.keys(venue.spaces).length > 0 && (
        <OptionInput
          title="Space"
          value={inquiry.spaces}
          onChange={value =>
            setInquiry({
              ...inquiry,
              spaces: value
            })
          }
          options={Object.keys(venue.spaces).map(spaceId => {
            const space = venue.spaces[spaceId];

            return {
              id: spaceId,
              title: space.name,
              description: `Capacity: ${space.capacity}`,
              cost: space.startingCost
            };
          })}
        />
      )}
      {Object.keys(venue.options).length > 0 && (
        <OptionInput
          title="Options"
          value={inquiry.options}
          onChange={value =>
            setInquiry({
              ...inquiry,
              options: value
            })
          }
          options={Object.keys(venue.options).map(optionId => {
            const option = venue.options[optionId];

            return {
              id: optionId,
              title: getOptionTitle(option),
              cost: option.startingCost
            };
          })}
        />
      )}
      <Input
        title={"Comments"}
        value={inquiry.comments}
        multiline={true}
        onChange={value => {
          setInquiry(i => ({
            ...i,
            comments: value
          }));
        }}
      />
      <LowerWrapper>
        <Pricing>
          {estimatedPrice.minimum && (
            <PriceMinimum
              style={
                estimatedPrice.total && Boolean(estimatedPrice.total.amount)
                  ? { marginBottom: 10 }
                  : {}
              }
            >
              <PriceMinimumTitle>Required Minimum Spend</PriceMinimumTitle>
              <PriceMinimumAmount>
                {displayPrice(estimatedPrice.minimum)}
              </PriceMinimumAmount>
            </PriceMinimum>
          )}
          {Boolean(estimatedPrice.total.amount) && (
            <PriceEstimate>
              <PriceEstimateTitle active={true}>
                Starting Price
              </PriceEstimateTitle>
              <PriceEstimateAmount>
                <LightningIcon
                  color={colors.primary}
                  size={17}
                  style={{ margin: "8px 5px 0 0" }}
                />
                {displayPrice(estimatedPrice.total)}
              </PriceEstimateAmount>
            </PriceEstimate>
          )}
          {!(
            estimatedPrice.minimum || Boolean(estimatedPrice.total.amount)
          ) && (
            <PriceEstimate>
              <PriceEstimateTitle active={false}>
                Starting Price
              </PriceEstimateTitle>
              <InstantQuote>
                <LightningIcon
                  color={colors.gray}
                  size={15}
                  style={{ margin: "5px 5px 0 0" }}
                />
                Instant Estimate
              </InstantQuote>
            </PriceEstimate>
          )}
        </Pricing>

        {!props.preview && (
          <Button
            title="Start Chat"
            submit={true}
            loading={loading}
            onClick={submitInquiry}
          />
        )}
      </LowerWrapper>
    </Wrapper>
  );
};
BookPricing.defaultProps = {
  preview: false
};

const mapState = (state: RootState, ownProps: RouteComponentProps) => {
  const { venueId } = ownProps.match.params as { venueId: string };

  return {
    venue: selectors.getVenue(venueId)(state),
    filters: selectors.searchFilters(state),
    loggedIn: selectors.loggedIn(state),
    loading: selectors.isCreatingChat(state)
  };
};
const mapDispatch = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      CreateChat
    },
    dispatch
  );
const connector = connect(mapState, mapDispatch);

export default withRouter(connector(BookPricing));
